import { Box, Radio, RadioGroup, Typography } from '@mui/material';
import ModalityChip from '~/components/Chip/ModalityChip';
import { FormattedMessage, useIntl } from 'react-intl';
import { CheckCircle } from '@mui/icons-material';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import { formatSeriesDate } from '~/utils/date';

type StepImportCompleteProps = {
    onBackToSelection: () => void;
    onClose: () => void;
};

function getImageMeta(image: AvailableImage) {
    const obj = image.imageObject;
    if (!obj) {
        return null;
    }
    const meta = obj.seriesMetadata;
    const slice = obj.firstSlice;
    return {
        description: meta.seriesDescription || image.label || '',
        date: meta.seriesDate ? formatSeriesDate(meta.seriesDate) : undefined,
        manufacturer: slice?.manufacturer || undefined,
        modelName: slice?.manufacturerModelName || undefined,
        stationName: slice?.stationName || undefined,
        kvp: slice?.kvp || undefined,
    };
}

export default function StepImportComplete({
    onBackToSelection: _onBackToSelection,
    onClose: _onClose,
}: StepImportCompleteProps) {
    const intl = useIntl();
    const t = (id: string) => intl.formatMessage({ id });
    const { availableImages, primaryImage, handleChangePrimaryImage } =
        usePrimaryImage(true);

    const selectedSeriesUID = primaryImage?.seriesUid ?? '';

    return (
        <Box display="flex" flexDirection="column" height="100%" gap={2}>
            <Typography variant="body2" fontWeight={500}>
                <FormattedMessage id="primaryImage" />
            </Typography>

            <RadioGroup value={selectedSeriesUID}>
                <Box display="grid" gap={0.5}>
                    {availableImages.map((image) => {
                        const selected =
                            image.seriesInstanceUID === selectedSeriesUID;
                        const meta = getImageMeta(image);
                        const desc = meta?.description || t('unknown.series');
                        const details = [
                            meta?.date,
                            meta?.manufacturer,
                            meta?.modelName,
                            meta?.stationName,
                            meta?.kvp,
                        ].filter(Boolean);

                        return (
                            <Box
                                key={image.seriesInstanceUID}
                                onClick={() => handleChangePrimaryImage(image)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    py: 0.75,
                                    px: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: selected
                                        ? 'primary.main'
                                        : 'divider',
                                    backgroundColor: selected
                                        ? 'action.selected'
                                        : 'background.paper',
                                    cursor: selected ? 'default' : 'pointer',
                                    '&:hover': selected
                                        ? {}
                                        : {
                                              backgroundColor: 'action.hover',
                                          },
                                }}
                            >
                                <Radio
                                    checked={selected}
                                    size="small"
                                    sx={{ p: 0.25, mt: 0.25 }}
                                />
                                <Box display="flex" mt={0.25}>
                                    <ModalityChip
                                        modality={image.modality}
                                        size="small"
                                        color={selected ? 'primary' : 'default'}
                                    />
                                </Box>
                                <Box
                                    flex={1}
                                    minWidth={0}
                                    display="grid"
                                    gap={0.25}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        noWrap
                                    >
                                        <SensitiveInfoTypography>
                                            {desc}
                                        </SensitiveInfoTypography>
                                    </Typography>
                                    {details.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            <SensitiveInfoTypography>
                                                {details.join(' · ')}
                                            </SensitiveInfoTypography>
                                        </Typography>
                                    )}
                                </Box>
                                {selected && (
                                    <CheckCircle
                                        sx={{
                                            color: 'primary.main',
                                            fontSize: 18,
                                            mt: 0.25,
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </RadioGroup>

            {availableImages.length === 0 && (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flex={1}
                >
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage id="noPatientDataAvailable" />
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
